import { Button, Divider, Grid, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { StyledEngineProvider } from '@mui/material/styles';
import { useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as GoogleLogo } from '../../components/Assets/google-logo.svg';
import { ReactComponent as Onedataicon } from '../../components/Assets/OnedataBlackicon.svg';
import { ReactComponent as TigerLogo } from '../../components/Assets/TA_logo_Black.svg';
import loginApi from '../../components/Utilities/apis/loginApi';
import { getUserReportsGroupByOrgName } from '../../components/Utilities/v2Apis/getUserReports';
import config from '../../config';
import {
  addReports,
  addSelectedReport,
  addUser,
  clearReportSlice,
} from '../../redux/userReducer';
import styles from './Login.module.css';
/**
 * Login Component
 * This component represents the login page of the application.
 * It allows users to sign in with Google OAuth and handles user validation.
 * If the user is validated, it stores user information and redirects to the reports page.
 */
const Login = ({ location }) => {
  const dispatch = useDispatch();
  const { state } = useLocation();
  const [isValidUser, setIsValidUser] = useState(false);
  const [Location, setLocation] = useState(null);
  const [loader, setLoader] = useState(false);
  const user = useSelector((state) => state.PowerBi.user);
  const navigate = useNavigate();

  // Open the connection to the API while the user is on the sign-in screen, so
  // /auth/google and /validateUserByEmail don't pay for DNS + TLS setup.
  // (Only a connection is opened — no request is sent.)
  useEffect(() => {
    const origin = new URL(config.apiURL).origin;
    if (document.querySelector(`link[rel="preconnect"][href="${origin}"]`))
      return;
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = origin;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    if (state && state.location.pathname) {
      setLocation(state.location.pathname);
    } else {
      setLocation(location);
    }
  }, [Location, state, location]);

  useEffect(() => {
    if (
      Object.keys(user).length !== 0 &&
      localStorage.getItem('refreshToken')
    ) {
      setLoader(true);
      const Access = user?.ACCESS_ROLE;

      if (Array.isArray(Access) && Access.length > 0) {
        // Navigate to the first valid PMS_ACCESS route
        navigate(`/${Access[0]}`);
      } else if (typeof Access === 'string' && Access.trim() !== '') {
        // Handle case where PMS_ACCESS is a single string
        navigate(`/${Access}`);
      } else {
        // Default fallback
        navigate('/reports');
      }
    }
  }, [navigate, user]);

  // The OAuthHandler is initialized using the useGoogleLogin hook. This hook is typically set up to provide OAuth authentication capabilities, including handling user login via Google.
  // The OAuthHandler function is responsible for handling the OAuth2 authentication flow with Google.
  // It retrieves user data from Google, validates the user on your server,
  // and takes appropriate actions based on the validation result, including storing user data in the Redux store and navigating the user to the reports page.Authorization,
  // or displaying an error message if needed.]
  // Then when the user logs in you redirect them back with
  const login = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      const code = codeResponse.code;
      setLoader(true);
      let tokens;
      try {
        tokens = await axios.post(`${config.apiURL}/auth/google`, { code });
      } catch (err) {
        setLoader(false);
        return;
      }
      localStorage.setItem('googleIdToken', tokens.data.id_token);
      localStorage.setItem('refreshToken', tokens.data.refresh_token);
      dispatch(addSelectedReport({}));
      dispatch(clearReportSlice());
      const result = await loginApi(tokens.data.id_token);
      if (result && Object.keys(result).length !== 0) {
        // The effect above navigates as soon as the user lands in the store.
        dispatch(addUser(result));
        // Load the report names in the background (not awaited, so navigation
        // isn't delayed). They're saved to the store, so the sidebar renders
        // instantly when the reports page opens; Home still refreshes them.
        if (result.ID) {
          getUserReportsGroupByOrgName(result.ID).then((reports) => {
            if (Array.isArray(reports) && reports.length) {
              dispatch(addReports(reports));
            }
          });
        }
      } else {
        dispatch(addUser(result || {}));
        setLoader(false);
        setIsValidUser(true);
      }
    },
    flow: 'auth-code',
    prompt: 'consent',
  });

  return (
    <StyledEngineProvider injectFirst>
      <div className={styles.app}>
        {loader ? (
          <>
            <CircularProgress style={{ color: '#f7901d' }} />
            <Typography style={{ color: 'white' }}>Checking Access</Typography>
          </>
        ) : (
          <>
            {isValidUser ? (
              <p className={styles.errMessage}>Forbidden: Access Denied.!</p>
            ) : (
              ''
            )}
            <Grid container className={styles.loginForm}>
              <Grid item md={12} className={styles.form}>
                <Onedataicon className={styles.onedataicon} />
              </Grid>
              <Grid item md={12} className={styles.signInBox}>
                <Grid item md={12} className={styles.signInBox}>
                  <Button className={styles.signInButton} onClick={login}>
                    <GoogleLogo className={styles.googleLogo} />
                    <span className={styles.buttonText}>
                      Sign In with Google
                    </span>
                  </Button>
                </Grid>
                {/* <Divider style={{width: '100%', marginBottom: '20px'}} /> */}
              </Grid>
              <Divider style={{ width: '100%', marginBottom: '20px' }} />
              <Grid container className={styles.footContainer}>
                <Grid item md={5}>
                  <a
                    href='https://www.tigeranalytics.com'
                    target='_blank'
                    rel='noreferrer'
                  >
                    <TigerLogo className={styles.tigerLogo} />
                  </a>
                </Grid>
                <Grid item md={7} className={styles.copyRight}>
                  Copyrights 2026 | All Rights Reserved
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
      </div>
    </StyledEngineProvider>
  );
};

export default Login;
